import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import MaterialReactTable from 'material-react-table';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import {
    encryptAES,
    fechaDate,
    nWithCommas,
    processFechaNM,
    convertirCamelCase,
    eliminarAcentos,
    getNombreMes,
    stringDateToTimestamp,
    agrupaMovFecha,
    negativos,
    TEXT_FIN_SESION_JWT,
    capitalizeFirstLetter,
    cambiaEfectivo
} from "../utils";
import { BASE_URL } from "../properties";
import {
    getSaldosFecha,
    solicitudFormatosConfiguracionInicial,
    descargarFormatoReporte
} from "../ws";
import {
    toogleLoad,
    setSaldosMovimientos,
    setTokenSesion,
    showModalFinSesion
} from "../redux/slices/appslice";
import { ReactComponent as SinMovimientosIcon } from '../assets/icons/sin_movimientos.svg';
import DualMonthCalendar from '../components/DualMonthCalendar';
import ModalVisorPdf from '../components/ModalVisorPdf';

const MESES = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
];

const MESES_ABREV = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/**
 * Convierte un objeto Date o timestamp a string YYYY-MM-DD
 */
const toDateString = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/**
 * Convierte un formato de fecha a string YYYY-MM-DD
 */
const extractDateString = (fechaRaw) => {
    if (!fechaRaw) return null;
    if (fechaRaw instanceof Date && !isNaN(fechaRaw.getTime())) {
        return toDateString(fechaRaw);
    }
    const str = String(fechaRaw).trim();
    if (str.length === 8 && /^\d{8}$/.test(str)) {
        return `${str.substring(0, 4)}-${str.substring(4, 6)}-${str.substring(6, 8)}`;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
        return str.substring(0, 10);
    }
    if (str.includes('/')) {
        const parts = str.split('/');
        if (parts.length === 3) {
            if (parts[0].length === 4) {
                return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
            }
            const y = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
            return `${y}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
    }
    if (typeof fechaRaw === 'number' && fechaRaw > 1000000000) {
        const d = new Date(fechaRaw);
        if (!isNaN(d.getTime())) return toDateString(d);
    }
    return null;
};

/**
 * Formatea YYYY-MM-DD a DD/MM/YYYY para los inputs
 */
const formatDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

/**
 * Formatea rango para el subtítulo: "22 Oct 2026 - 30 Oct 2026"
 */
const formatDateRangeText = (startStr, endStr) => {
    if (!startStr || !endStr) return '';
    const pStart = startStr.split('-');
    const pEnd = endStr.split('-');
    if (pStart.length === 3 && pEnd.length === 3) {
        const d1 = pStart[2];
        const m1 = MESES_ABREV[parseInt(pStart[1], 10) - 1] || pStart[1];
        const y1 = pStart[0];
        const d2 = pEnd[2];
        const m2 = MESES_ABREV[parseInt(pEnd[1], 10) - 1] || pEnd[1];
        const y2 = pEnd[0];
        return `${d1} ${m1} ${y1} - ${d2} ${m2} ${y2}`;
    }
    return `${startStr} - ${endStr}`;
};

/**
 * Formatea una fecha a formato dia/mes/año (DD/MM/YYYY)
 */
const formatDiaMesAnio = (fechaRaw) => {
    if (!fechaRaw) return '-';
    const str = String(fechaRaw).trim();
    if (str.length === 8 && /^\d{8}$/.test(str)) {
        return `${str.substring(6, 8)}-${str.substring(4, 6)}-${str.substring(2, 4)}`;
    }
    if (str.includes('-')) {
        const p = str.split('T')[0].split('-');
        if (p.length === 3) {
            return `${p[2].padStart(2, '0')}/${p[1].padStart(2, '0')}/${p[0]}`;
        }
    }
    if (str.includes('/')) {
        const p = str.split('/');
        if (p.length === 3) {
            if (p[0].length === 4) {
                return `${p[2].padStart(2, '0')}/${p[1].padStart(2, '0')}/${p[0]}`;
            }
            const y = p[2].length === 2 ? `${p[2]}` : p[2];
            return `${p[0].padStart(2, '0')}-${p[1].padStart(2, '0')}-${y}`;
        }
    }
    return str;
};

/**
 * Formatea un monto a formato de moneda ($#,###.## o -$#,###.##)
 */
const formatMoneda = (val) => {
    if (val === undefined || val === null || val === '') return '-';
    const num = typeof val === 'string' ? parseFloat(val.replace(/,/g, '')) : Number(val);
    if (isNaN(num)) return '-';
    return negativos(`$${nWithCommas(num.toFixed(2))}`);
};

function SaldosMovimientos() {
    const appData = useSelector((state) => state.appData);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();

    // Navegación de pestañas: 'historial' | 'saldos' (Default: 'historial' según maqueta PANTALLA_CONSULTAS.png)
    const [activeTab, setActiveTab] = useState('historial');

    // Configuración del producto y retorno
    const [titulo, setTitulo] = useState('');
    const [toReturn, setToReturn] = useState('/');

    // Estados de Notificación Toast
    const [message, setMessage] = useState(false);

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                setMessage(false);
            }, 6000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    // -------------------------------------------------------------------------
    // ESTADOS PARA PESTAÑA 1: HISTORIAL DE MOVIMIENTOS
    // -------------------------------------------------------------------------
    const [allSaldos, setAllSaldos] = useState([]);
    const [filteredSaldos, setFilteredSaldos] = useState([]);
    const [minDateHistorial, setMinDateHistorial] = useState('');
    const [maxDateHistorial, setMaxDateHistorial] = useState('');
    const [rangeStart, setRangeStart] = useState(null);
    const [rangeEnd, setRangeEnd] = useState(null);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [consultedRange, setConsultedRange] = useState(null);

    // -------------------------------------------------------------------------
    // ESTADOS PARA PESTAÑA 2: REPORTE DE SALDOS Y MOVIMIENTOS
    // -------------------------------------------------------------------------
    const [mesSeleccionado, setMesSeleccionado] = useState('');
    const [anioSeleccionado, setAnioSeleccionado] = useState('');
    const [aniosDisponibles, setAniosDisponibles] = useState([]);
    const [movimientos, setMovimientos] = useState([]);
    const [movToShow, setMovToShow] = useState({});
    const [hasConsultedSaldos, setHasConsultedSaldos] = useState(false);
    const [saldosFecha, setSaldosFecha] = useState(false);
    const [formatosReporte, setFormatosReporte] = useState(null);
    const [filtros, setFiltros] = useState({ del: null, hasta: null, mes: null, anio: null });

    // -------------------------------------------------------------------------
    // ESTADOS PARA MODAL VISOR DE PDF
    // -------------------------------------------------------------------------
    const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
    const [pdfBase64Data, setPdfBase64Data] = useState(null);

    // -------------------------------------------------------------------------
    // CARGA INICIAL
    // -------------------------------------------------------------------------
    useEffect(() => {
        dispatch(toogleLoad(true));

        let currentTitulo = '';
        let currentReturn = '/';

        if (params.tipo === 'fondodeahorro') {
            currentTitulo = 'Fondo de Ahorro';
            currentReturn = '/fondodeahorro';
        } else if (params.tipo === 'cajadeahorro') {
            currentTitulo = 'Caja de Ahorro';
            currentReturn = '/cajadeahorro';
        } else if (params.tipo === 'plandepensiones') {
            currentTitulo = 'Fondo de Pensiones';
            currentReturn = '/plandepensiones';
        } else {
            navigate('/');
            return;
        }

        setTitulo(currentTitulo);
        setToReturn(currentReturn);

        // Años para los últimos 24 meses en el selector
        const now = new Date();
        const curY = now.getFullYear();
        setAniosDisponibles([curY, curY - 1, curY - 2]);

        cargarDatosCompletos(currentTitulo, currentReturn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.tipo]);

    const cargarDatosCompletos = async (prodTitulo, prodReturn) => {
        try {
            // 1. Obtener Saldos Fecha
            const sfRes = await getSaldosFecha(appData, () => {
                dispatch(showModalFinSesion({ texto: TEXT_FIN_SESION_JWT }));
            });

            let saldoProducto = null;
            if (sfRes?.Datos && Array.isArray(sfRes.Datos)) {
                for (const dato of sfRes.Datos) {
                    if (dato.Producto?.[0]?.TipoCliente?.toUpperCase() === prodTitulo.toUpperCase()) {
                        saldoProducto = dato;
                        setSaldosFecha(dato);
                        break;
                    }
                }
            }

            if (sfRes?.JWT?.current_token) {
                dispatch(setTokenSesion(sfRes.JWT.current_token));
            }

            // 2. Configuración de Formatos de Reporte (para descarga y visor PDF)
            if (saldoProducto?.Producto?.[0]) {
                const formatosRes = await solicitudFormatosConfiguracionInicial(appData, saldoProducto.Producto[0], () => {
                    dispatch(showModalFinSesion({ texto: TEXT_FIN_SESION_JWT }));
                });
                if (formatosRes?.Datos?.lstFormatosS) {
                    setFormatosReporte(formatosRes.Datos.lstFormatosS);
                }
            }

            // 3. Obtener Movimientos detallados (/saldos/movimientos) para Historial y Reporte
            await obtenerMovimientosDetallados(prodTitulo, prodReturn);

        } catch (err) {
            console.error("Error al cargar datos de consultas:", err);
            setMessage({
                texto: 'Ocurrió un error al cargar la información.',
                tipo: 'error',
                icono: 'close'
            });
        } finally {
            dispatch(toogleLoad(false));
        }
    };

    /**
     * Procesa los movimientos del servicio /saldos/movimientos para la Pestaña 1 (Historial)
     */
    const procesarHistorialMovimientos = (ordenados) => {
        setAllSaldos(ordenados);

        // Determinar fecha mínima y fecha máxima de los movimientos
        const dates = ordenados.map(r => r._dateStr).filter(Boolean);
        if (dates.length > 0) {
            dates.sort();
            const minD = dates[0];
            const maxD = dates[dates.length - 1];

            const todayStr = toDateString(new Date());
            const realMax = maxD > todayStr ? maxD : todayStr;

            setMinDateHistorial(minD);
            setMaxDateHistorial(realMax);

            // Preseleccionar por defecto el rango completo disponible
            setRangeStart(minD);
            setRangeEnd(maxD);
            setConsultedRange({ start: minD, end: maxD });
            setFilteredSaldos(ordenados);
        } else {
            const todayStr = toDateString(new Date());
            setMinDateHistorial('2024-01-01');
            setMaxDateHistorial(todayStr);
            setRangeStart(null);
            setRangeEnd(null);
            setConsultedRange(null);
            setFilteredSaldos([]);
        }
    };

    /**
     * Consulta del endpoint /saldos/movimientos para la Pestaña 2
     */
    const obtenerMovimientosDetallados = async (prodTitulo, prodReturn) => {
        try {
            const dataToEncrypt = {
                "Usuario": appData.user?.usuarioLogin,
                "IdUsuario": `${appData.user?.IdUsuario}`,
                "Sesion": appData.user?.Sesion,
                "DeviceToken": "",
                "SistemaOperativo": "ios",
                "AppID": "SAF_APP",
                "token": appData.tokenSesion,
                "refresh_token": appData.token?.refresh_token
            };

            const requestOptions = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': appData.idToken
                },
                body: JSON.stringify({ DatosEncriptados: encryptAES(dataToEncrypt) })
            };

            const response = await fetch(`${BASE_URL}/saldos/movimientos`, requestOptions);
            const data = await response.json();

            if (data?.BanderaExito && Array.isArray(data?.Datos)) {
                dispatch(setSaldosMovimientos(data.Datos));

                for (const dato of data.Datos) {
                    if (dato.Producto?.[0]?.TipoCliente?.toUpperCase() === prodTitulo.toUpperCase()) {
                        let movs = (dato.MovimientoAhorro || []).concat(dato.MovimientoPrestamo || []);
                        movs = JSON.parse(JSON.stringify(movs));

                        procesarMovimientosMensuales(movs);
                        break;
                    }
                }

                if (data?.JWT?.current_token) {
                    dispatch(setTokenSesion(data.JWT.current_token));
                }
            }
        } catch (error) {
            console.error("Error al obtener movimientos detallados:", error);
        }
    };

    const procesarMovimientosMensuales = (movsList) => {
        if (!movsList || !movsList.length) {
            setMovimientos([]);
            setMovToShow({});
            setAllSaldos([]);
            setFilteredSaldos([]);
            setMinDateHistorial('2024-01-01');
            setMaxDateHistorial(toDateString(new Date()));
            setRangeStart(null);
            setRangeEnd(null);
            setConsultedRange(null);
            return;
        }

        const aniosSet = new Set();
        movsList.forEach(m => {
            if (m.hasOwnProperty('Concepto') && m.hasOwnProperty('Cuenta')) {
                m['descrip'] = `${m.Concepto} - ${m.Cuenta}`;
                m['descripPDF'] = `${m.Cuenta}`;
            } else if (m.hasOwnProperty('Concepto')) {
                m['descrip'] = `${m.Concepto}`;
                m['descripPDF'] = `${m.Concepto}`;
            }
            m['date'] = fechaDate(m.Fecha);
            m['timestamp'] = stringDateToTimestamp(m.Fecha);
            m['_dateStr'] = extractDateString(m.Fecha);

            if (m.Fecha && m.Fecha.length >= 4) {
                const a = parseInt(m.Fecha.substring(0, 4), 10);
                if (!isNaN(a)) aniosSet.add(a);
            }
        });

        // Asegurar últimos 24 meses
        const curY = new Date().getFullYear();
        aniosSet.add(curY);
        aniosSet.add(curY - 1);
        const sortedAnios = Array.from(aniosSet).sort((a, b) => b - a);
        setAniosDisponibles(sortedAnios);

        // Orden descendente por timestamp
        const ordenados = [...movsList].sort((a, b) => b.timestamp - a.timestamp);
        setMovimientos(ordenados);

        // Procesar movimientos para la tabla de Historial (Pestaña 1)
        procesarHistorialMovimientos(ordenados);

        // Preseleccionar por defecto el mes y año más reciente
        if (ordenados.length > 0) {
            const masReciente = ordenados[0];
            const anioIni = parseInt(masReciente.Fecha.substring(0, 4), 10);
            const mesIni = parseInt(masReciente.Fecha.substring(4, 6), 10);

            setMesSeleccionado(mesIni);
            setAnioSeleccionado(anioIni);

            const fInicio = new Date(anioIni, mesIni - 1, 1, 0, 0, 0, 0);
            const fFin = new Date(anioIni, mesIni, 0, 23, 59, 59, 999);
            const startTs = fInicio.getTime();
            const endTs = fFin.getTime();

            setFiltros({
                del: startTs,
                hasta: endTs,
                mes: mesIni,
                anio: anioIni
            });

            const movsMes = ordenados.filter(m => m.timestamp >= startTs && m.timestamp <= endTs);
            setMovToShow(agrupaMovFecha(movsMes));
            setHasConsultedSaldos(true);
        }
    };

    // -------------------------------------------------------------------------
    // HANDLERS DE PESTAÑA 1 (HISTORIAL)
    // -------------------------------------------------------------------------
    const handleSelectRange = (start, end) => {
        setRangeStart(start);
        setRangeEnd(end);
    };

    const handleConsultarHistorial = () => {
        if (!rangeStart || !rangeEnd) return;

        const start = rangeStart <= rangeEnd ? rangeStart : rangeEnd;
        const end = rangeStart <= rangeEnd ? rangeEnd : rangeStart;

        const sourceList = allSaldos && allSaldos.length > 0 ? allSaldos : movimientos;
        const filtrados = sourceList.filter(item => {
            if (!item._dateStr) return true;
            return item._dateStr >= start && item._dateStr <= end;
        });

        setFilteredSaldos(filtrados);
        setConsultedRange({ start, end });
        setIsCalendarOpen(false);
    };

    const handleExportCSVHistorial = useCallback(() => {
        if (!filteredSaldos || filteredSaldos.length === 0) return;

        const csvConfig = mkConfig({
            fieldSeparator: ',',
            decimalSeparator: '.',
            useKeysAsHeaders: true,
            filename: `Movimientos_${(titulo || 'Movimientos').replace(/ /g, '_')}`,
        });

        const exportData = filteredSaldos.map((row) => {
            return {
                'Fecha': formatDiaMesAnio(row.Fecha || row._dateStr),
                'Concepto': row.Cuenta || '-',
                'Tipo de Movimiento': row.Concepto || '-',
                'Monto': formatMoneda(row.Monto)
            };
        });

        const csv = generateCsv(csvConfig)(exportData);
        download(csvConfig)(csv);

        setMessage({
            texto: '¡Listo! Se ha descargado el reporte en tu dispositivo.',
            tipo: 'exito'
        });
    }, [filteredSaldos, titulo]);

    // Columnas de la tabla para Pestaña 1
    const getSortIcon = (column) => {
        const isSorted = column.getIsSorted();
        if (isSorted === 'desc') return 'arrow_downward';
        if (isSorted === 'asc') return 'arrow_upward';
        return 'unfold_more';
    };

    const renderHeader = useCallback((label, column) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', cursor: 'pointer' }}>
            <span>{label}</span>
            <i className="material-icons" style={{ fontSize: '16px', marginLeft: '4px', color: column.getIsSorted() ? '#202A52' : '#9e9e9e' }}>
                {getSortIcon(column)}
            </i>
        </div>
    ), []);

    const columnsHistorial = useMemo(() => [
        {
            accessorKey: 'Fecha',
            header: 'Fecha',
            Header: ({ column }) => renderHeader('Fecha', column),
            Cell: ({ cell, row }) => formatDiaMesAnio(cell.getValue() || row.original.Fecha || row.original._dateStr),
            sortingFn: (rowA, rowB) => {
                const tA = rowA.original.timestamp || 0;
                const tB = rowB.original.timestamp || 0;
                return tA - tB;
            }
        },
       
        {
            accessorKey: 'Cuenta',
            header: 'Cuenta',
            Header: ({ column }) => renderHeader('Concepto', column),
            Cell: ({ cell, row }) => {
                const val = cell.getValue() !== undefined && cell.getValue() !== null ? cell.getValue() : row.original.Cuenta;
                return val || '-';
            },
            sortingFn: (rowA, rowB) => {
                const cA = rowA.original.Cuenta || '';
                const cB = rowB.original.Cuenta || '';
                return cA.localeCompare(cB);
            }
        },
         {
            accessorKey: 'Concepto',
            header: 'Concepto',
            Header: ({ column }) => renderHeader('Tipo de Movimiento', column),
            Cell: ({ cell, row }) => {
                const val = cell.getValue() !== undefined && cell.getValue() !== null ? cell.getValue() : row.original.Concepto;
                return val || '-';
            }
        },
        {
            accessorKey: 'Monto',
            header: 'Monto',
            Header: ({ column }) => renderHeader('Monto', column),
            Cell: ({ cell, row }) => {
                const val = cell.getValue() !== undefined && cell.getValue() !== null ? cell.getValue() : row.original.Monto;
                const num = typeof val === 'string' ? parseFloat(val.replace(/,/g, '')) : Number(val);
                const isNegative = (!isNaN(num) && num < 0) || (row.original?.Concepto && eliminarAcentos(row.original.Concepto.toLowerCase()) === 'prestamo');
                return (
                    <span style={{ color: isNegative ? '#d32f2f' : '#202A52', fontWeight: 600 }}>
                        {formatMoneda(val)}
                    </span>
                );
            },
            sortingFn: (rowA, rowB) => {
                const mA = Number(rowA.original.Monto) || 0;
                const mB = Number(rowB.original.Monto) || 0;
                return mA - mB;
            }
        }
    ], [renderHeader]);

    // -------------------------------------------------------------------------
    // HANDLERS DE PESTAÑA 2 (REPORTE DE SALDOS Y MOVIMIENTOS)
    // -------------------------------------------------------------------------
    const handleConsultarSaldos = () => {
        setMessage(false);

        if (!mesSeleccionado || !anioSeleccionado) {
            setMessage({
                texto: 'Seleccione un mes y un año para realizar la consulta.',
                tipo: 'error',
                icono: 'info_outline'
            });
            return;
        }

        const mesNum = parseInt(mesSeleccionado, 10);
        const anioNum = parseInt(anioSeleccionado, 10);

        const fechaInicio = new Date(anioNum, mesNum - 1, 1, 0, 0, 0, 0);
        const fechaFin = new Date(anioNum, mesNum, 0, 23, 59, 59, 999);
        const startTimestamp = fechaInicio.getTime();
        const endTimestamp = fechaFin.getTime();

        setFiltros({
            del: startTimestamp,
            hasta: endTimestamp,
            mes: mesNum,
            anio: anioNum
        });

        const copyMovimientos = [...movimientos];
        const movimientosMes = copyMovimientos.filter(v => v.timestamp >= startTimestamp && v.timestamp <= endTimestamp);

        if (movimientosMes.length === 0) {
            const nombreMes = getNombreMes(mesNum - 1) || mesNum;
            setMessage({
                texto: `No existe información para ${nombreMes} de ${anioNum}.`,
                tipo: 'error',
                icono: 'info_outline'
            });
            setMovToShow({});
            setHasConsultedSaldos(true);
            return;
        }

        movimientosMes.sort((a, b) => b.timestamp - a.timestamp);
        setMovToShow(agrupaMovFecha(movimientosMes));
        setHasConsultedSaldos(true);
    };

    /**
     * Descarga directa del archivo PDF
     */
    const handleDescargarPDF = async () => {
        if (!filtros?.del || !filtros?.hasta) {
            setMessage({
                texto: 'Seleccione un periodo válido para descargar.',
                tipo: 'error',
                icono: 'info_outline'
            });
            return;
        }

        if (!formatosReporte || !formatosReporte.length) {
            setMessage({
                texto: 'No hay formatos de reporte disponibles.',
                tipo: 'error',
                icono: 'info_outline'
            });
            return;
        }

        dispatch(toogleLoad(true));
        try {
            const formatDateParam = (ts) => {
                const d = new Date(ts);
                return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
            };

            const idFormato = formatosReporte[0].IdFormato;
            const dProducto = saldosFecha?.Producto?.[0];

            const res = await descargarFormatoReporte(
                appData,
                idFormato,
                formatDateParam(filtros.del),
                formatDateParam(filtros.hasta),
                dProducto,
                () => {
                    dispatch(showModalFinSesion({ texto: TEXT_FIN_SESION_JWT }));
                }
            );

            if (res?.Datos?.DatosArchivo) {
                const linkSource = `data:application/pdf;base64,${res.Datos.DatosArchivo}`;
                const downloadLink = document.createElement("a");
                downloadLink.href = linkSource;
                downloadLink.download = `${(titulo === 'Fondo de Pensiones' ? 'Plan de Pensiones' : titulo) || 'Reporte'}.pdf`;
                downloadLink.click();

                setMessage({
                    texto: '¡Listo! Se ha descargado el reporte en tu dispositivo.',
                    tipo: 'exito',
                    icono: 'check'
                });
            } else {
                setMessage({
                    texto: 'Error al generar el reporte en PDF.',
                    tipo: 'error',
                    icono: 'close'
                });
            }
        } catch (e) {
            console.error(e);
            setMessage({
                texto: 'Error al procesar la descarga.',
                tipo: 'error',
                icono: 'close'
            });
        } finally {
            dispatch(toogleLoad(false));
        }
    };

    /**
     * Abre el modal visor con el PDF generado
     */
    const handleVerReportePDF = async () => {
        if (!filtros?.del || !filtros?.hasta) {
            setMessage({
                texto: 'Seleccione un periodo válido para consultar.',
                tipo: 'error',
                icono: 'info_outline'
            });
            return;
        }

        if (!formatosReporte || !formatosReporte.length) {
            setMessage({
                texto: 'No hay formatos de reporte disponibles.',
                tipo: 'error',
                icono: 'info_outline'
            });
            return;
        }

        dispatch(toogleLoad(true));
        try {
            const formatDateParam = (ts) => {
                const d = new Date(ts);
                return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
            };

            const idFormato = formatosReporte[0].IdFormato;
            const dProducto = saldosFecha?.Producto?.[0];

            const res = await descargarFormatoReporte(
                appData,
                idFormato,
                formatDateParam(filtros.del),
                formatDateParam(filtros.hasta),
                dProducto,
                () => {
                    dispatch(showModalFinSesion({ texto: TEXT_FIN_SESION_JWT }));
                }
            );

            if (res?.Datos?.DatosArchivo) {
                setPdfBase64Data(res.Datos.DatosArchivo);
                setIsPdfModalOpen(true);
                setMessage({
                    texto: '¡Listo! Se ha generado el reporte.',
                    tipo: 'exito',
                    icono: 'check'
                });
            } else {
                setMessage({
                    texto: 'No se pudo visualizar el reporte.',
                    tipo: 'error',
                    icono: 'close'
                });
            }
        } catch (e) {
            console.error(e);
            setMessage({
                texto: 'Error al obtener el reporte.',
                tipo: 'error',
                icono: 'close'
            });
        } finally {
            dispatch(toogleLoad(false));
        }
    };

    // Nombre dinámico del producto para el resumen (Fondo / Caja / Plan)
    const nombreResumenProducto = useMemo(() => {
        if (titulo === 'Fondo de Pensiones') return 'Plan';
        if (titulo === 'Caja de Ahorro') return 'Caja';
        return 'Fondo';
    }, [titulo]);

    // Subtítulo de fecha de corte para el resumen
    const fechaCorteResumen = useMemo(() => {
        if (saldosFecha?.FechaOP) {
            const s = String(saldosFecha.FechaOP);
            if (s.length === 8) {
                const m = parseInt(s.substring(4, 6), 10);
                const nombreM = MESES[m - 1]?.label || s.substring(4, 6);
                return `${s.substring(6, 8)}/${nombreM}/${s.substring(0, 4)}`;
            }
        }
        const now = new Date();
        const m = MESES[now.getMonth()]?.label;
        return `${now.getDate()}/${m}/${now.getFullYear()}`;
    }, [saldosFecha]);

    return (
        <div className="container" style={{ marginTop: '2rem', maxWidth: '1100px', minHeight: 'calc(100vh - 120px)', paddingBottom: '3rem' }}>
            <div className="row">
                <div className="col s12">
                    {/* Mensaje Toast de Éxito / Error */}
                    {message && (
                        <div
                            style={{
                                backgroundColor: message.tipo === 'error' ? '#ef5350' : '#86CB7B',
                                color: 'white',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '1.5rem',
                                fontSize: '15px',
                                fontWeight: 700,
                                boxShadow: '0 2px 5px rgba(0,0,0,0.08)'
                            }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginRight: '12px', flexShrink: 0 }}>
                                <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none" />
                                <path d="M8 12.5l2.5 2.5L16 9.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                            <span>{message.texto}</span>
                        </div>
                    )}

                    {/* Cabecera Superior (Header Global) con Breadcrumb, Título "Consultas" y Botón "✕ Cerrar" */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <p
                            className="cursorPointer"
                            onClick={() => navigate(toReturn)}
                            style={{
                                color: '#64748b',
                                margin: '0 0 8px 0',
                                display: 'inline-flex',
                                alignItems: 'center',
                                fontSize: '14px'
                            }}
                        >
                            <i className="material-icons" style={{ fontSize: '18px', marginRight: '4px' }}>arrow_back</i>
                            {titulo === 'Fondo de Pensiones' ? 'Plan de Pensiones' : titulo}
                        </p>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <h4 className="txtBold" style={{ color: '#202A52', margin: 0, fontWeight: 700, fontSize: '32px' }}>
                                Consultas
                            </h4>
                            <button
                                type="button"
                                className="btn white"
                                onClick={() => navigate(toReturn)}
                                style={{
                                    color: '#202A52',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    fontWeight: 600,
                                    padding: '0 16px',
                                    height: '38px',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                <i className="material-icons" style={{ fontSize: '18px', marginRight: '6px', color: '#202A52' }}>close</i> Cerrar
                            </button>
                        </div>
                    </div>

                    {/* ========================================================= */}
                    {/* CUADRO PRINCIPAL UNIFICADO QUE ENCIERRA TODO EL MÓDULO    */}
                    {/* ========================================================= */}
                    <div style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '2.5rem 2.5rem 3rem 2.5rem'
                    }}>
                        {/* Subtítulo Orientativo */}
                        <p style={{ color: '#202A52', fontSize: '16px', fontWeight: 600, margin: '0 0 1.25rem 0' }}>
                            Selecciona la consulta que deseas realizar
                        </p>

                        {/* Barra de Selección de Pestañas (Tabs) */}
                        <div style={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '0 24px',
                            display: 'flex',
                            gap: '32px',
                            alignItems: 'center',
                            marginBottom: '2.5rem',
                            height: '56px'
                        }}>
                            {/* Pestaña 1: Historial de movimientos */}
                            <div
                                onClick={() => setActiveTab('historial')}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    height: '100%',
                                    borderBottom: activeTab === 'historial' ? '3px solid #202A52' : '3px solid transparent',
                                    color: activeTab === 'historial' ? '#202A52' : '#94a3b8',
                                    fontWeight: activeTab === 'historial' ? '700' : '500',
                                    padding: '0 4px',
                                    fontSize: '15px',
                                    userSelect: 'none'
                                }}
                            >
                                <i className="material-icons" style={{ fontSize: '20px', marginRight: '8px', color: activeTab === 'historial' ? '#202A52' : '#94a3b8' }}>history</i>
                                <span>Historial de movimientos</span>
                            </div>

                            {/* Pestaña 2: Reporte de saldos y movimientos */}
                            <div
                                onClick={() => setActiveTab('saldos')}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    height: '100%',
                                    borderBottom: activeTab === 'saldos' ? '3px solid #202A52' : '3px solid transparent',
                                    color: activeTab === 'saldos' ? '#202A52' : '#94a3b8',
                                    fontWeight: activeTab === 'saldos' ? '700' : '500',
                                    padding: '0 4px',
                                    fontSize: '15px',
                                    userSelect: 'none'
                                }}
                            >
                                <i className="material-icons" style={{ fontSize: '20px', marginRight: '8px', color: activeTab === 'saldos' ? '#202A52' : '#94a3b8' }}>description</i>
                                <span>Reporte de saldos y movimientos</span>
                            </div>
                        </div>

                        {/* ========================================================= */}
                        {/* CONTENIDO PESTAÑA 1: HISTORIAL DE MOVIMIENTOS             */}
                        {/* ========================================================= */}
                        {activeTab === 'historial' && (
                            <div>
                                <h5 className="txtBold" style={{ color: '#202A52', margin: 0, fontWeight: 700, fontSize: '20px' }}>
                                    Consulta tu historial
                                </h5>
                                <p style={{ color: '#64748b', fontSize: '14px', margin: '6px 0 1.5rem 0' }}>
                                    Selecciona un período para ver el detalle de tus movimientos.
                                </p>

                                <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                    {/* Campo Desde */}
                                    <div style={{ flex: 1, minWidth: '220px' }}>
                                        <label style={{ color: '#64748b', fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                                            Desde
                                        </label>
                                        <div
                                            onClick={() => setIsCalendarOpen(prev => !prev)}
                                            style={{
                                                height: '44px',
                                                backgroundColor: '#f8fafc',
                                                border: isCalendarOpen ? '1px solid #202A52' : '1px solid #e2e8f0',
                                                borderBottom: isCalendarOpen ? '2px solid #202A52' : '1px solid #e2e8f0',
                                                borderRadius: '6px',
                                                padding: '0 1rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                color: rangeStart ? '#202A52' : '#94a3b8',
                                                fontSize: '14px',
                                                fontWeight: rangeStart ? 600 : 400
                                            }}
                                        >
                                            <span>{rangeStart ? formatDDMMYYYY(rangeStart) : 'DD/MM/YYYY'}</span>
                                            <i className="material-icons" style={{ color: '#202A52', fontSize: '20px' }}>expand_more</i>
                                        </div>
                                    </div>

                                    {/* Campo Hasta */}
                                    <div style={{ flex: 1, minWidth: '220px' }}>
                                        <label style={{ color: '#64748b', fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                                            Hasta
                                        </label>
                                        <div
                                            onClick={() => setIsCalendarOpen(prev => !prev)}
                                            style={{
                                                height: '44px',
                                                backgroundColor: '#f8fafc',
                                                border: isCalendarOpen ? '1px solid #202A52' : '1px solid #e2e8f0',
                                                borderBottom: isCalendarOpen ? '2px solid #202A52' : '1px solid #e2e8f0',
                                                borderRadius: '6px',
                                                padding: '0 1rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                color: rangeEnd ? '#202A52' : '#94a3b8',
                                                fontSize: '14px',
                                                fontWeight: rangeEnd ? 600 : 400
                                            }}
                                        >
                                            <span>{rangeEnd ? formatDDMMYYYY(rangeEnd) : 'DD/MM/YYYY'}</span>
                                            <i className="material-icons" style={{ color: '#202A52', fontSize: '20px' }}>expand_more</i>
                                        </div>
                                    </div>

                                    {/* Botón Consultar */}
                                    <div>
                                        <button
                                            type="button"
                                            className="btn"
                                            disabled={!rangeStart || !rangeEnd}
                                            onClick={handleConsultarHistorial}
                                            style={{
                                                backgroundColor: (rangeStart && rangeEnd) ? '#202A52' : '#cbd5e1',
                                                color: 'white',
                                                textTransform: 'none',
                                                borderRadius: '8px',
                                                height: '44px',
                                                padding: '0 2.5rem',
                                                boxShadow: 'none',
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                cursor: (rangeStart && rangeEnd) ? 'pointer' : 'default'
                                            }}
                                        >
                                            Consultar
                                        </button>
                                    </div>
                                </div>

                                {/* Componente Calendario Dual Interactivo */}
                                <DualMonthCalendar
                                    isOpen={isCalendarOpen}
                                    minDate={minDateHistorial}
                                    maxDate={maxDateHistorial}
                                    startDate={rangeStart}
                                    endDate={rangeEnd}
                                    onSelectRange={handleSelectRange}
                                />

                                {/* Línea divisoria sutil hacia la sección de resultados */}
                                <div style={{ borderTop: '1px solid #f1f5f9', margin: '2.5rem 0 2rem 0' }} />

                                {/* Sección de Resultados: Movimientos del período */}
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                                    <div>
                                        <h6 className="txtBold" style={{ color: '#202A52', margin: 0, fontWeight: 700, fontSize: '18px' }}>
                                            Movimientos del período
                                        </h6>
                                        <span style={{ color: '#64748b', fontSize: '14px' }}>
                                            {consultedRange ? formatDateRangeText(consultedRange.start, consultedRange.end) : 'Selecciona un rango de fechas'}
                                        </span>
                                    </div>

                                    {/* Botón Descargar CSV (o SCV de maqueta) */}
                                    <button
                                        type="button"
                                        className="btn white"
                                        onClick={handleExportCSVHistorial}
                                        disabled={!filteredSaldos || filteredSaldos.length === 0}
                                        style={{
                                            color: '#202A52',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            boxShadow: 'none',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            height: '38px',
                                            padding: '0 16px',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: (filteredSaldos && filteredSaldos.length > 0) ? 'pointer' : 'default',
                                            opacity: (filteredSaldos && filteredSaldos.length > 0) ? 1 : 0.6
                                        }}
                                        title="Descargar información en archivo CSV"
                                    >
                                        <i className="material-icons" style={{ fontSize: '18px', marginRight: '6px', color: '#202A52' }}>description</i> Descargar CSV
                                    </button>
                                </div>

                                {/* Tabla MaterialReactTable con Paridad Visual de ListaRetiros.js */}
                                {filteredSaldos.length === 0 ? (
                                    <div className="center-align" style={{ padding: '3rem 1rem', border: '1px solid #e0e0e0', borderRadius: '8px', marginTop: '1rem' }}>
                                        <SinMovimientosIcon style={{ maxWidth: '100%', height: 'auto', margin: '2rem 0' }} />
                                        <p style={{ color: '#666', fontSize: '18px' }}>No se encontraron movimientos en este periodo</p>
                                    </div>
                                ) : (
                                    <MaterialReactTable
                                        columns={columnsHistorial}
                                        data={filteredSaldos}
                                        enableColumnFilters={false}
                                        enableDensityToggle={false}
                                        enableFullScreenToggle={false}
                                        enablePagination={true}
                                        enableSorting={true}
                                        enableColumnActions={false}
                                        enableTopToolbar={false}
                                        enableBottomToolbar={true}
                                        initialState={{
                                            pagination: { pageSize: 6, pageIndex: 0 }
                                        }}
                                        renderBottomToolbar={({ table }) => {
                                            const pageIndex = table.getState().pagination.pageIndex;
                                            const pageCount = Math.max(table.getPageCount(), 1);
                                            const canPrev = table.getCanPreviousPage();
                                            const canNext = table.getCanNextPage();

                                            return (
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    padding: '1.25rem 0',
                                                    gap: '8px',
                                                    borderTop: '1px solid #e0e0e0',
                                                    backgroundColor: '#ffffff',
                                                    borderRadius: '0 0 8px 8px'
                                                }}>
                                                    {/* Primera página: << */}
                                                    <button
                                                        type="button"
                                                        disabled={!canPrev}
                                                        onClick={() => table.setPageIndex(0)}
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#ffffff',
                                                            border: canPrev ? '1.5px solid #202A52' : '1px solid #cbd5e1',
                                                            color: canPrev ? '#202A52' : '#cbd5e1',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: canPrev ? 'pointer' : 'default',
                                                            outline: 'none',
                                                            padding: 0,
                                                            boxShadow: 'none',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        title="Primera página"
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="11 17 6 12 11 7" />
                                                            <polyline points="18 17 13 12 18 7" />
                                                        </svg>
                                                    </button>

                                                    {/* Página anterior: < */}
                                                    <button
                                                        type="button"
                                                        disabled={!canPrev}
                                                        onClick={() => table.previousPage()}
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#ffffff',
                                                            border: canPrev ? '1.5px solid #202A52' : '1px solid #cbd5e1',
                                                            color: canPrev ? '#202A52' : '#cbd5e1',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: canPrev ? 'pointer' : 'default',
                                                            outline: 'none',
                                                            padding: 0,
                                                            boxShadow: 'none',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        title="Página anterior"
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="15 18 9 12 15 6" />
                                                        </svg>
                                                    </button>

                                                    {/* Texto Página X de Y */}
                                                    <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 500, margin: '0 16px' }}>
                                                        Página {pageIndex + 1} de {pageCount}
                                                    </span>

                                                    {/* Página siguiente: > */}
                                                    <button
                                                        type="button"
                                                        disabled={!canNext}
                                                        onClick={() => table.nextPage()}
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#ffffff',
                                                            border: canNext ? '1.5px solid #202A52' : '1px solid #cbd5e1',
                                                            color: canNext ? '#202A52' : '#cbd5e1',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: canNext ? 'pointer' : 'default',
                                                            outline: 'none',
                                                            padding: 0,
                                                            boxShadow: 'none',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        title="Página siguiente"
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="9 18 15 12 9 6" />
                                                        </svg>
                                                    </button>

                                                    {/* Última página: >> */}
                                                    <button
                                                        type="button"
                                                        disabled={!canNext}
                                                        onClick={() => table.setPageIndex(pageCount - 1)}
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#ffffff',
                                                            border: canNext ? '1.5px solid #202A52' : '1px solid #cbd5e1',
                                                            color: canNext ? '#202A52' : '#cbd5e1',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: canNext ? 'pointer' : 'default',
                                                            outline: 'none',
                                                            padding: 0,
                                                            boxShadow: 'none',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        title="Última página"
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="13 17 18 12 13 7" />
                                                            <polyline points="6 17 11 12 6 7" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            );
                                        }}
                                        muiTablePaperProps={{
                                            elevation: 0,
                                            style: { border: '1px solid #e0e0e0', borderRadius: '8px' }
                                        }}
                                        muiTableHeadCellProps={{
                                            align: 'left',
                                            sx: {
                                                backgroundColor: '#F6F8FA',
                                                color: '#666',
                                                fontWeight: 'bold',
                                                borderBottom: '1px solid #e0e0e0',
                                                textAlign: 'left',
                                                '& .MuiTableSortLabel-icon': {
                                                    display: 'none',
                                                }
                                            }
                                        }}
                                        muiTableBodyCellProps={{
                                            align: 'left',
                                            sx: {
                                                textAlign: 'left'
                                            }
                                        }}
                                        muiTableBodyRowProps={({ row }) => {
                                            const rawMonto = row.original?.Monto;
                                            const num = typeof rawMonto === 'string' ? parseFloat(rawMonto.replace(/,/g, '')) : Number(rawMonto);
                                            const isNegative = (!isNaN(num) && num < 0) || (row.original?.Concepto && eliminarAcentos(row.original.Concepto.toLowerCase()) === 'prestamo');

                                            return {
                                                sx: {
                                                    backgroundColor: isNegative ? '#FDE8E8' : 'inherit',
                                                    '&:hover': {
                                                        backgroundColor: isNegative ? '#fad7d7' : '#f8fafc',
                                                    }
                                                }
                                            };
                                        }}
                                        localization={{
                                            ...MRT_Localization_ES,
                                            noRecordsToDisplay: 'No hay movimientos para mostrar'
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* ========================================================= */}
                    {/* CONTENIDO PESTAÑA 2: REPORTE DE SALDOS Y MOVIMIENTOS      */}
                    {/* ========================================================= */}
                    {activeTab === 'saldos' && (
                        <div>
                            <h5 className="txtBold" style={{ color: '#202A52', margin: 0, fontWeight: 700, fontSize: '20px' }}>
                                Filtrar movimientos
                            </h5>
                            <p style={{ color: '#64748b', fontSize: '14px', margin: '6px 0 1.5rem 0' }}>
                                Consulta los reportes de los últimos 24 meses están para su descarga. Para consultar reportes anteriores, contacta a tu área de recursos humanos.
                            </p>

                            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                {/* Selector de Año */}
                                <div style={{ flex: 1, minWidth: '220px', position: 'relative' }}>
                                    <label style={{ color: '#64748b', fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                                        Año
                                    </label>
                                    <select
                                        className="browser-default"
                                        value={anioSeleccionado}
                                        onChange={(e) => setAnioSeleccionado(e.target.value)}
                                        style={{
                                            width: '100%',
                                            height: '44px',
                                            backgroundColor: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            padding: '0 2.5rem 0 1rem',
                                            color: anioSeleccionado ? '#202A52' : '#94a3b8',
                                            fontSize: '14px',
                                            outline: 'none',
                                            appearance: 'none',
                                            fontWeight: anioSeleccionado ? 600 : 400
                                        }}
                                    >
                                        <option value="">Selecciona una opción</option>
                                        {aniosDisponibles.map(a => (
                                            <option key={a} value={a} style={{ color: '#202A52' }}>{a}</option>
                                        ))}
                                    </select>
                                    <i className="material-icons" style={{ position: 'absolute', right: '12px', top: '34px', pointerEvents: 'none', color: '#202A52' }}>expand_more</i>
                                </div>

                                {/* Selector de Mes */}
                                <div style={{ flex: 1, minWidth: '220px', position: 'relative' }}>
                                    <label style={{ color: '#64748b', fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                                        Mes
                                    </label>
                                    <select
                                        className="browser-default"
                                        value={mesSeleccionado}
                                        onChange={(e) => setMesSeleccionado(e.target.value)}
                                        style={{
                                            width: '100%',
                                            height: '44px',
                                            backgroundColor: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            padding: '0 2.5rem 0 1rem',
                                            color: mesSeleccionado ? '#202A52' : '#94a3b8',
                                            fontSize: '14px',
                                            outline: 'none',
                                            appearance: 'none',
                                            fontWeight: mesSeleccionado ? 600 : 400
                                        }}
                                    >
                                        <option value="">Selecciona una opción</option>
                                        {MESES.map(m => (
                                            <option key={m.value} value={m.value} style={{ color: '#202A52' }}>{m.label}</option>
                                        ))}
                                    </select>
                                    <i className="material-icons" style={{ position: 'absolute', right: '12px', top: '34px', pointerEvents: 'none', color: '#202A52' }}>expand_more</i>
                                </div>

                                {/* Botón Consultar */}
                                <div>
                                    <button
                                        type="button"
                                        className="btn"
                                        disabled={!mesSeleccionado || !anioSeleccionado}
                                        onClick={handleConsultarSaldos}
                                        style={{
                                            backgroundColor: (mesSeleccionado && anioSeleccionado) ? '#202A52' : '#cbd5e1',
                                            color: 'white',
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            height: '44px',
                                            padding: '0 2.5rem',
                                            boxShadow: 'none',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            cursor: (mesSeleccionado && anioSeleccionado) ? 'pointer' : 'default'
                                        }}
                                    >
                                        Consultar
                                    </button>
                                </div>
                            </div>

                            {/* Resultados de la Consulta: Resumen en Cards y Movimientos */}
                            {hasConsultedSaldos && (
                                <div>
                                    {/* Línea divisoria sutil como en VISTA_INFORMACION_SYM.png */}
                                    <div style={{ borderTop: '1px solid #f1f5f9', margin: '2.5rem 0 2rem 0' }} />

                                    {/* Encabezado del Resumen y Botones 'Ver reporte' y 'Descargar PDF' */}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                                       
                                     <div style={{ display: 'flex', gap: '12px' }}>
                                            {/* Botón 'Ver reporte' */}
                                            <button
                                                type="button"
                                                className="btn white"
                                                onClick={handleVerReportePDF}
                                                style={{
                                                    color: '#202A52',
                                                    border: '1px solid #cbd5e1',
                                                    borderRadius: '8px',
                                                    textTransform: 'none',
                                                    boxShadow: 'none',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    height: '38px',
                                                    padding: '0 16px',
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer'
                                                }}
                                                title="Visualizar reporte de saldos y movimientos"
                                            >
                                                <i className="material-icons" style={{ fontSize: '18px', marginRight: '6px', color: '#202A52' }}>visibility</i> Ver reporte
                                            </button>

                                            {/* Botón 'Descargar PDF' */}
                                            <button
                                                type="button"
                                                className="btn white"
                                                onClick={handleDescargarPDF}
                                                style={{
                                                    color: '#202A52',
                                                    border: '1px solid #cbd5e1',
                                                    borderRadius: '8px',
                                                    textTransform: 'none',
                                                    boxShadow: 'none',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    height: '38px',
                                                    padding: '0 16px',
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer'
                                                }}
                                                title="Descargar reporte en formato PDF"
                                            >
                                                <i className="material-icons" style={{ fontSize: '18px', marginRight: '6px', color: '#202A52' }}>description</i> Descargar PDF
                                            </button>
                                        </div>
                                    </div>


                                    {/* Sección de Movimientos Detallados del Fondo */}
                                    <div>
                                        <h6 className="txtBold" style={{ color: '#202A52', margin: '0 0 1rem 0', fontWeight: 700, fontSize: '16px' }}>
                                            Movimientos de mi {nombreResumenProducto}
                                        </h6>

                                        {movToShow && Object.keys(movToShow).length > 0 ? (
                                            <div>
                                                {Object.keys(movToShow).reverse().map((fechaKey, i) => (
                                                    <div key={i} style={{ marginBottom: '1.25rem' }}>
                                                        {/* Cabecera de Fecha */}
                                                        <div style={{
                                                            color: '#202A52',
                                                            fontSize: '13px',
                                                            fontWeight: 700,
                                                            marginBottom: '8px'
                                                        }}>
                                                            {processFechaNM(fechaKey)}
                                                        </div>

                                                        {/* Renglones de Movimientos */}
                                                        {movToShow[fechaKey].map((d, j) => {
                                                            const isNegative = d.Monto < 0 || (d.Concepto && eliminarAcentos(d.Concepto.toLowerCase()) === 'prestamo');

                                                            return (
                                                                <div
                                                                    key={j}
                                                                    style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        padding: '10px 16px',
                                                                        borderRadius: '6px',
                                                                        marginBottom: '6px',
                                                                        backgroundColor: isNegative ? '#FDE8E8' : '#F8FAFC'
                                                                    }}
                                                                >
                                                                    <div style={{
                                                                        color: '#202A52',
                                                                        fontSize: '13px',
                                                                        fontWeight: 500,
                                                                        textTransform: 'capitalize'
                                                                    }}>
                                                                        {cambiaEfectivo(capitalizeFirstLetter(convertirCamelCase(d.descrip)))}
                                                                    </div>

                                                                    <div style={{
                                                                        color: isNegative ? '#d32f2f' : '#202A52',
                                                                        fontSize: '14px',
                                                                        fontWeight: 700
                                                                    }}>
                                                                        {eliminarAcentos(d.Concepto?.toLowerCase() || '') === 'prestamo' ? '-' : ''}
                                                                        {negativos(`$${nWithCommas(parseFloat(d.Monto).toFixed(2))}`)}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div style={{
                                                padding: '2.5rem 1rem',
                                                textAlign: 'center',
                                                color: '#64748b',
                                                fontSize: '15px'
                                            }}>
                                                No se encontraron movimientos registrados para este período.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    </div>
                </div>
            </div>

            {/* Modal Visor de PDF */}
            <ModalVisorPdf
                isOpen={isPdfModalOpen}
                onClose={() => setIsPdfModalOpen(false)}
                pdfBase64={pdfBase64Data}
                title="Reporte de Saldos y Movimientos"
            />
        </div>
    );
}

export default SaldosMovimientos;